import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, primeCsrfToken } from "../api/client";

export default function Login() {
  const navigate = useNavigate();
  const { setUser, setIsAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [needsVerifyResend, setNeedsVerifyResend] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleResendVerification = async () => {
    try {
      await primeCsrfToken();
      await api.auth.resendVerification({ email: email.trim() });
      setError("Verification email sent. Check your inbox.");
      setNeedsVerifyResend(false);
    } catch {
      setError("Could not send email. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[Login] Form submitted');
    setError("");
    setNeedsVerifyResend(false);
    setLoading(true);

    try {
      console.log('[Login] Calling login API...');
      const response = await api.auth.login(email, password);
      console.log('[Login] Login successful, token:', response.data.token?.substring(0, 20) + '...');
      const { token, user } = response.data;

      localStorage.setItem("jwt_token", token);
      localStorage.setItem("user", JSON.stringify(user));
      console.log('[Login] Token stored in localStorage');

      // Set auth state directly — no /auth/me call needed
      setUser(user);
      setIsAuthenticated(true);
      console.log('[Login] Auth state set, navigating to /Dashboard');
      navigate("/Dashboard");
    } catch (err) {
      console.log('[Login] Login error:', err);
      const data = err.response?.data;
      const code = data?.error;
      const message =
        data?.message ||
        data?.error ||
        "Login failed. Please try again.";
      const msgStr = String(message);
      const lower = msgStr.toLowerCase();
      const showResend =
        code === "EMAIL_NOT_VERIFIED" ||
        lower.includes("verify") ||
        lower.includes("confirm");
      setNeedsVerifyResend(showResend);
      setError(msgStr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ad-bg">
      <div className="w-full max-w-md bg-white border border-ad-border rounded-[10px] p-8 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Agent David"
            className="w-12 h-12 object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">Agent David</h1>
          <p className="text-gray-600 mt-1">Sign in to your account</p>
        </div>

        {needsVerifyResend && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            Please verify your email before signing in.
            <br />
            <button
              type="button"
              onClick={handleResendVerification}
              className="underline font-medium mt-1 inline-block"
            >
              Resend verification email
            </button>
          </div>
        )}
        {error && !needsVerifyResend && (
          <div
            className={`mb-4 p-3 rounded-md text-sm border ${ error.includes("Verification email sent") ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700" }`}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 bg-white border border-ad-border text-gray-900 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-ad-primary/30"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-600 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 bg-white border border-ad-border text-gray-900 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-ad-primary/30"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-ad-primary text-white rounded-[8px] hover:bg-ad-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ad-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400 mt-4">
          Don&apos;t have an account?{" "}
          <a
            href="/signup"
            className="text-ad-primary font-medium hover:underline"
          >
            Start free trial
          </a>
        </p>
      </div>
    </div>
  );
}
